import { LogOut, ShieldAlert } from 'lucide-react'
import { useEffect, useState, type ReactNode } from 'react'

import { Button } from '@aeris/ui'

import { useAuth } from '../lib/auth-context.ts'

/**
 * A faixa que diz, ao OPERADOR, que ele não está na própria conta (F169).
 *
 * ## Para quem ela é, e para quem não é
 *
 * Para quem entrou. O lojista não vê nada — é a decisão do dono registrada no
 * adendo da ADR-0035, e ela é o motivo de a única garantia restante ser a
 * auditoria. Quem for acrescentar o aviso do outro lado um dia mexe aqui e na
 * sessão dele, não só aqui.
 *
 * ## Por que ela empurra a tela em vez de flutuar sobre ela
 *
 * Uma faixa flutuante cobre conteúdo, e a primeira coisa que ela cobriria é a
 * barra de topo — justamente onde está o nome da empresa, que é o dado que
 * responde "de quem é esta tela?". `--support-banner-h` sai de `0px` para
 * `2.25rem` enquanto ela existe, e as três molduras já descontam esse valor
 * da altura útil.
 *
 * ## Por que o relógio corre aqui
 *
 * O prazo é conferido no SERVIDOR, a cada requisição. Este contador não
 * autoriza nada — ele evita a situação em que o operador acha que ainda está
 * dentro e descobre que não estava ao clicar em salvar. Quando zera, pede a
 * sessão de novo e a faixa some sozinha, porque o servidor já devolveu a
 * sessão à conta de origem.
 */

const remainingLabel = (expiresAt: string, now: number): string => {
  const left = new Date(expiresAt).getTime() - now
  if (Number.isNaN(left) || left <= 0) return 'acabando'

  const minutes = Math.floor(left / 60_000)
  if (minutes >= 1) return `${minutes} min`
  return `${Math.floor(left / 1_000)} s`
}

export const SupportBanner = (): ReactNode => {
  const { support, exitSupport, signOut } = useAuth()
  const [now, setNow] = useState(() => Date.now())
  const [leaving, setLeaving] = useState(false)

  const active = support !== null

  /*
   * O atributo vive no `<html>`, e não numa classe deste componente: quem
   * precisa da altura são as molduras, que são irmãs desta faixa na árvore.
   */
  useEffect(() => {
    const root = document.documentElement
    if (!active) {
      delete root.dataset.support
      return
    }

    root.dataset.support = 'on'
    return () => {
      delete root.dataset.support
    }
  }, [active])

  useEffect(() => {
    if (!active) return
    const timer = window.setInterval(() => {
      setNow(Date.now())
    }, 1_000)
    return () => {
      window.clearInterval(timer)
    }
  }, [active])

  if (support === null) return null

  const leave = (): void => {
    setLeaving(true)
    void exitSupport()
      .catch(async () => {
        /*
         * A saída falhou — e ficar preso dentro da conta de um cliente é o
         * pior desfecho possível aqui. Sair da sessão inteira resolve em
         * qualquer caso: o empréstimo morre com ela.
         */
        await signOut()
      })
      .finally(() => {
        setLeaving(false)
      })
  }

  return (
    <div
      role="status"
      className="fixed inset-x-0 top-0 z-(--z-tooltip) flex h-(--support-banner-h) items-center justify-center gap-3 bg-(--danger) px-4 text-sm font-medium text-white"
    >
      <ShieldAlert aria-hidden className="size-4 shrink-0" />
      <span className="truncate">
        Você está dentro de <strong>{support.tenantName}</strong> como suporte ·
        termina em {remainingLabel(support.expiresAt, now)}
      </span>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        onClick={leave}
        disabled={leaving}
        className="shrink-0 text-white hover:bg-white/20"
      >
        <LogOut aria-hidden className="size-4" />
        {leaving ? 'Saindo…' : 'Sair'}
      </Button>
    </div>
  )
}
